/**
 * V3 Quiz Funnel — Flow Router
 *
 * Step sequence (24 screens):
 *  1. motivation        — Motivation preference (single-select)
 *  2. motivation_intro  — Motivation educational interstitial (auto-advance 5s)
 *  3. age              — Age selection (30-39 / 40-49 / 50-59 / 60+)
 *  4. activity          — Activity level (single-select)
 *  5. interstitial_metabolism — Dark interstitial (auto-advance 6s)
 *  6. stress_symptoms   — Stress symptom picker (multi-select)
 *  7. sleep             — Sleep quality (single-select)
 *  8. physical_barriers — Physical barriers (multi-select)
 *  9. interstitial_science — Zone 2 science interstitial (auto-advance 6s)
 * 10. walking_history   — Walking experience (single-select)
 * 11. weight_pattern    — Weight pattern (single-select)
 * 12. interstitial_reframe — Cortisol loop interstitial (auto-advance 6s)
 * 13. primary_goal      — Primary goal (single-select)
 * 14. main_barrier      — Main barrier (single-select)
 * 15. reward_preference — Reward brand (single-select)
 * 16. email_capture     — Name + email form
 * 17. plan_loading      — Animated plan-building loader (5s)
 * 18. results           — 6-section personalized conversion page
 * 19. paywall           — Plan selection
 * 20. payment           — Stripe checkout (card + Apple/Google Pay)
 * 21. create_account    — Password creation + registerUser
 * 22. coaching_upsell   — Coaching add-on (soft skip)
 * 23. exclusive_offer   — Exclusive sign-up offer (one-time purchase, soft skip)
 * 24. get_app           — App Store / Play Store download
 */

import React, { useRef, useEffect, useLayoutEffect, useState, useCallback, Suspense } from 'react';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import {
    JOIN_PLAN_BASE_PRICES_CENTS,
    resolveExpressCheckoutAmountCents,
} from './pricing/joinPlanPricing';
import { useFunnelV3Store, FunnelV3Step, PlanType } from '../../store/funnelV3Store';
import FunnelV3ProgressBar from './FunnelV3ProgressBar';
import { FUNNEL_V3_STEP_ORDER, getQuestionSteps } from './funnelV3StepConfig';
import { trackStepViewed, trackQuizCompleted, trackPageView } from './analytics/funnelV3Analytics';
import { ensureAbDefaultPlanVariant } from '../../lib/abDefaultPlan';
import { hydrateQuizFromProfile } from '../../utils/hydrateQuizFromProfile';
import { captureReferralCodeFromUrl } from '../../utils/utm';
import { validateReferralCode } from '../../services/api';
import {
    trackReferralBannerCodeInvalid,
    trackReferralBannerShown,
} from './analytics/funnelV3Analytics';
import { preloadStripe, StripeProvider } from './StripeProvider';
import { ErrorBoundary, PaymentErrorFallback } from '../ErrorBoundary';
import * as Sentry from '@sentry/react';
import { lazyWithRetry } from '../../utils/lazyWithRetry';

// Keep only the landing step in the initial bundle. The 1.7 second
// affirmation after selection gives us time to prefetch its successor.
import MotivationStep from './steps/MotivationStep';
const MotivationInterstitial = lazyWithRetry(() => import('./steps/MotivationInterstitial'), 'motivation_intro');
const GoalsIntroStep = lazyWithRetry(() => import('./steps/GoalsIntroStep'), 'goals_intro');
const AgeStep = lazyWithRetry(() => import('./steps/AgeStep'), 'age');
const ActivityStep = lazyWithRetry(() => import('./steps/ActivityStep'), 'activity');
const InterstitialMetabolism = lazyWithRetry(() => import('./steps/InterstitialMetabolism'), 'interstitial_metabolism');
const StressSymptomsStep = lazyWithRetry(() => import('./steps/StressSymptomsStep'), 'stress_symptoms');
const SleepStep = lazyWithRetry(() => import('./steps/SleepStep'), 'sleep');
const InterstitialSleep = lazyWithRetry(() => import('./steps/InterstitialSleep'), 'interstitial_sleep');
const PhysicalBarriersStep = lazyWithRetry(() => import('./steps/PhysicalBarriersStep'), 'physical_barriers');
const TargetZonesStep = lazyWithRetry(() => import('./steps/TargetZonesStep'), 'target_zones');
const InterstitialScience = lazyWithRetry(() => import('./steps/InterstitialScience'), 'interstitial_science');
const HeightStep = lazyWithRetry(() => import('./steps/HeightStep'), 'height');
const CurrentWeightStep = lazyWithRetry(() => import('./steps/CurrentWeightStep'), 'current_weight');
const GoalWeightStep = lazyWithRetry(() => import('./steps/GoalWeightStep'), 'goal_weight');
const BirthYearStep = lazyWithRetry(() => import('./steps/BirthYearStep'), 'birth_year');
const TargetDateStep = lazyWithRetry(() => import('./steps/TargetDateStep'), 'target_date');
const WeightProjectionStep = lazyWithRetry(() => import('./steps/WeightProjectionStep'), 'weight_projection');
const WalkingHistoryStep = lazyWithRetry(() => import('./steps/WalkingHistoryStep'), 'walking_history');
const WeightPatternStep = lazyWithRetry(() => import('./steps/WeightPatternStep'), 'weight_pattern');
const InterstitialReframe = lazyWithRetry(() => import('./steps/InterstitialReframe'), 'interstitial_reframe');
const PrimaryGoalStep = lazyWithRetry(() => import('./steps/PrimaryGoalStep'), 'primary_goal');
const MainBarrierStep = lazyWithRetry(() => import('./steps/MainBarrierStep'), 'main_barrier');
const PersonalizedBlockersStep = lazyWithRetry(() => import('./steps/PersonalizedBlockersStep'), 'personalized_blockers');
const ExitIntentOverlay = lazyWithRetry(() => import('./paywall/ExitIntentOverlay'), 'payment_exit_overlay');

// Late-funnel steps — lazy loaded (user won't reach these for minutes)
const CommitmentCheckStep = lazyWithRetry(() => import('./steps/CommitmentCheckStep'), 'commitment_check');
const TestimonialReviewStep = lazyWithRetry(() => import('./steps/TestimonialReviewStep'), 'testimonial_review');
const InterstitialRewards = lazyWithRetry(() => import('./steps/InterstitialRewards'), 'interstitial_rewards');
const RewardPreferenceStep = lazyWithRetry(() => import('./steps/RewardPreferenceStep'), 'reward_preference');
const ReviewLoadingStep = lazyWithRetry(() => import('./steps/ReviewLoadingStep'), 'review_loading');
const EmailCaptureStep = lazyWithRetry(() => import('./steps/EmailCaptureStep'), 'email_capture');
const ReferralRewardsStep = lazyWithRetry(() => import('./steps/ReferralRewardsStep'), 'referral_rewards');
const JourneyStoryStep = lazyWithRetry(() => import('./steps/JourneyStoryStep'), 'journey_story');
const PlanLoadingStep = lazyWithRetry(() => import('./steps/PlanLoadingStep'), 'plan_loading');
const StartingPointStep = lazyWithRetry(() => import('./steps/StartingPointStep'), 'starting_point');
const PlanStagesStep = lazyWithRetry(() => import('./steps/PlanStagesStep'), 'plan_stages');
const ExpectedResultsStep = lazyWithRetry(() => import('./steps/ExpectedResultsStep'), 'expected_results');
const ResultsPage = lazyWithRetry(() => import('./steps/ResultsPage'), 'results');
const PaywallStep = lazyWithRetry(() => import('./steps/PaywallStep'), 'paywall');

// Post-paywall — lazy loaded (includes Stripe, account creation, etc.)
// Keep raw import functions so we can prefetch chunks early
const paymentStepImport = () => import('./steps/PaymentStep');
const createAccountStepImport = () => import('./steps/CreateAccountStep');
const coachingUpsellStepImport = () => import('./steps/CoachingUpsellStep');
const exclusiveOfferStepImport = () => import('./steps/ExclusiveOfferStep');

const PaymentStep = lazyWithRetry(paymentStepImport, 'payment');
const CreateAccountStep = lazyWithRetry(createAccountStepImport, 'create_account');
const CoachingUpsellStep = lazyWithRetry(coachingUpsellStepImport, 'coaching_upsell');
const ExclusiveOfferStep = lazyWithRetry(exclusiveOfferStepImport, 'exclusive_offer');
const GetAppStep = lazyWithRetry(() => import('./steps/GetAppStep'), 'get_app');

// Number of quiz input steps completed before review loading starts.
const QUIZ_INPUT_STEPS = getQuestionSteps('review_loading').length;

const POST_PAYMENT_STEPS = new Set<FunnelV3Step>(['create_account', 'coaching_upsell', 'exclusive_offer', 'get_app']);
const PAYMENT_STEPS = new Set<FunnelV3Step>(['payment', 'paywall']);

// Resolve deferred-payment Elements options for the ExpressCheckoutElement on PaymentStep.
// Derives amount/currency from store data only, no plan cache dependency.
function buildPaymentElementsOptions(storeData: any): StripeElementsOptions | undefined {
    const selectedPlan = storeData.selectedPlan as string | null;
    if (!selectedPlan || !(selectedPlan in JOIN_PLAN_BASE_PRICES_CENTS)) return undefined;

    const chargeAmount = resolveExpressCheckoutAmountCents({
        selectedPlan: selectedPlan as keyof typeof JOIN_PLAN_BASE_PRICES_CENTS,
        exitDiscountActive: storeData.exitDiscountActive,
        promoDiscountPercent: storeData.promoDiscountPercent,
        referralDiscountPercent: storeData.referralDiscountPercent,
    });

    return {
        mode: 'payment',
        amount: chargeAmount,
        currency: 'usd',
        paymentMethodCreation: 'manual',
    };
}

const FunnelV3Flow: React.FC = () => {
    const { currentStep, setStep, data, updateField } = useFunnelV3Store();
    const prevStepRef = useRef<FunnelV3Step>(currentStep);
    const [isAnimating, setIsAnimating] = useState(false);
    const quizStartedAtRef = useRef(Date.now());
    const quizCompletedFiredRef = useRef(false);

    // Exit intent state for payment→paywall back-button intercept
    const [showPaymentExitOverlay, setShowPaymentExitOverlay] = useState(false);

    const handlePaymentBeforeBack = useCallback((): boolean => {
        if (!data.exitDiscountActive) {
            setShowPaymentExitOverlay(true);
            return true; // intercept navigation
        }
        return false; // discount already active, proceed normally
    }, [data.exitDiscountActive]);

    const handlePaymentExitClaim = useCallback(() => {
        updateField('exitDiscountActive', true);
        setShowPaymentExitOverlay(false);
        updateField('backFromPayment', true);
        setStep('paywall');
    }, [updateField, setStep]);

    const handlePaymentExitDismiss = useCallback(() => {
        setShowPaymentExitOverlay(false);
        updateField('backFromPayment', true);
        setStep('paywall');
    }, [updateField, setStep]);

    // Fire Meta PageView once on mount — required for pixel activation
    useEffect(() => { trackPageView(); }, []);

    // Capture coupon code from URL early (before EmailCaptureStep) so deep-links work
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const promoCode = params.get('promo') || params.get('coupon') || sessionStorage.getItem('couponCode');
        if (promoCode) {
            const code = promoCode.toUpperCase();
            sessionStorage.setItem('couponCode', code);
            updateField('stripeCouponCode', code);
        }
    }, [updateField]);

    // Persist ?ref= / /join/:code into the quiz store and validate for the landing banner.
    useEffect(() => {
        const code = captureReferralCodeFromUrl();
        if (!code) {
            return;
        }
        updateField('promoCode', code);
        let cancelled = false;
        void (async () => {
            const result = await validateReferralCode(code);
            if (cancelled) {
                return;
            }
            if (result.isValid) {
                if (result.referrerFirstName) {
                    updateField('referrerFirstName', result.referrerFirstName);
                    trackReferralBannerShown({ referrerFirstName: result.referrerFirstName });
                }
                return;
            }
            trackReferralBannerCodeInvalid();
        })();
        return () => {
            cancelled = true;
        };
    }, [updateField]);

    // Deep-link: ?step=paywall lets an authenticated user skip straight to the paywall
    // (e.g. returning via an email link processed by TokenAuthProvider)
    useEffect(() => {
        const step = new URLSearchParams(window.location.search).get('step');
        if (step === 'paywall') {
            // Small delay to let TokenAuthProvider finish writing tokens to localStorage
            const timer = setTimeout(() => {
                const token = localStorage.getItem('accessToken');
                if (!token) return;
                // Refill the quiz from the saved profile first: on a device that
                // never ran the quiz the paywall would otherwise project weights
                // the member never entered.
                void hydrateQuizFromProfile().then(() => setStep('paywall'));
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [setStep]);

    // Deep-link: ?step=email_capture drops an abandoned-cart lead on the email
    // screen. Deliberately ungated, unlike ?step=paywall — mail apps open links
    // in their own browser, so these visitors arrive with no session, and
    // submitting an address we already know signs them in and routes to the
    // paywall on its own. No token to mint, expire, or get burned by a scanner.
    const emailCaptureDeepLinkDoneRef = useRef(false);
    useEffect(() => {
        if (emailCaptureDeepLinkDoneRef.current) return;
        const step = new URLSearchParams(window.location.search).get('step');
        if (step !== 'email_capture') return;
        emailCaptureDeepLinkDoneRef.current = true;
        if (data.paymentCompleted) return;
        let hasToken = false;
        try {
            hasToken = !!localStorage.getItem('accessToken');
        } catch { /* storage disabled */ }
        if (!hasToken) {
            setStep('email_capture');
            return;
        }
        void hydrateQuizFromProfile().then(() => setStep('paywall'));
    }, [data.paymentCompleted, setStep]);

    // Guard: redirect to payment if user lands on a post-payment step without having paid
    useEffect(() => {
        if (POST_PAYMENT_STEPS.has(currentStep) && !data.paymentCompleted) {
            setStep('payment');
        }
    }, [currentStep, data.paymentCompleted, setStep]);

    // Assign sticky default-plan A/B variant on first paywall entry (50/50 control vs 12-week default)
    useEffect(() => {
        if (currentStep === 'paywall') {
            ensureAbDefaultPlanVariant();
        }
    }, [currentStep]);

    // Track every step view (centralized — fires once per step change)
    useEffect(() => {
        const stepNumber = FUNNEL_V3_STEP_ORDER.indexOf(currentStep) + 1;
        trackStepViewed(currentStep, stepNumber);

        // Fire quiz_completed once when all quiz questions are answered
        if (currentStep === 'review_loading' && !quizCompletedFiredRef.current) {
            quizCompletedFiredRef.current = true;
            trackQuizCompleted({
                totalQuestionsAnswered: QUIZ_INPUT_STEPS,
                totalTimeMs: Date.now() - quizStartedAtRef.current,
                primaryGoal: data.primaryGoal,
                painPoints: [
                    ...data.stressSymptoms,
                    ...(data.physicalBarriers || []),
                ],
                activityLevel: data.activityLevel,
                ageRange: data.ageGroup,
                walkingHistory: data.walkingHistory,
                weightPattern: data.weightPattern,
                currentWeightKg: data.currentWeightKg,
                goalWeightKg: data.goalWeightKg,
                heightCm: data.heightCm,
                birthYear: data.birthYear,
                stressSymptoms: data.stressSymptoms,
                sleepQuality: data.sleepQuality,
                physicalBarriers: data.physicalBarriers,
                targetZones: data.targetZones,
                mainBarrier: data.mainBarrier,
                motivation: data.motivation,
                rewardPreference: data.rewardPreference,
            });
        }
    }, [currentStep, data]);

    // Scroll to top + fade animation on every step change
    // useLayoutEffect prevents the 1-frame flash of new content before opacity-0
    useLayoutEffect(() => {
        if (prevStepRef.current !== currentStep) {
            window.scrollTo(0, 0);
            setIsAnimating(true);
            prevStepRef.current = currentStep;
        }
    }, [currentStep]);

    useEffect(() => {
        if (isAnimating) {
            const timer = setTimeout(() => setIsAnimating(false), 50);
            return () => clearTimeout(timer);
        }
    }, [isAnimating]);

    // Preload Stripe.js + payment chunk when user reaches paywall
    const prefetchedRef = useRef(false);
    useEffect(() => {
        if (currentStep === 'paywall' && !prefetchedRef.current) {
            prefetchedRef.current = true;
            preloadStripe();
            paymentStepImport();
        }
    }, [currentStep]);

    // Browser history — push entries so back button goes to previous step
    // After payment success, skip over payment/paywall to prevent duplicate charges
    const handlePopState = useCallback(() => {
        const currIndex = FUNNEL_V3_STEP_ORDER.indexOf(currentStep);
        if (currIndex <= 0) return;

        let targetIndex = currIndex - 1;

        // If payment is completed, skip past payment-related steps
        if (data.paymentCompleted) {
            while (targetIndex >= 0 && PAYMENT_STEPS.has(FUNNEL_V3_STEP_ORDER[targetIndex])) {
                targetIndex--;
            }
        }

        if (targetIndex >= 0) {
            setStep(FUNNEL_V3_STEP_ORDER[targetIndex]);
        }
    }, [currentStep, setStep, data.paymentCompleted]);

    useEffect(() => {
        window.history.pushState({ step: currentStep }, '', '');
    }, [currentStep]);

    useEffect(() => {
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [handlePopState]);

    const renderStep = () => {
        switch (currentStep) {
            case 'motivation':
                return <MotivationStep />;
            case 'motivation_intro':
                return <MotivationInterstitial />;
            case 'goals_intro':
                return <GoalsIntroStep />;
            case 'age':
                return <AgeStep />;
            case 'activity':
                return <ActivityStep />;
            case 'interstitial_metabolism':
                return <InterstitialMetabolism />;
            case 'stress_symptoms':
                return <StressSymptomsStep />;
            case 'sleep':
                return <SleepStep />;
            case 'interstitial_sleep':
                return <InterstitialSleep />;
            case 'physical_barriers':
                return <PhysicalBarriersStep />;
            case 'target_zones':
                return <TargetZonesStep />;
            case 'interstitial_science':
                return <InterstitialScience />;
            case 'height':
                return <HeightStep />;
            case 'current_weight':
                return <CurrentWeightStep />;
            case 'goal_weight':
                return <GoalWeightStep />;
            case 'birth_year':
                return <BirthYearStep />;
            case 'target_date':
                return <TargetDateStep />;
            case 'weight_projection':
                return <WeightProjectionStep />;
            case 'walking_history':
                return <WalkingHistoryStep />;
            case 'weight_pattern':
                return <WeightPatternStep />;
            case 'interstitial_reframe':
                return <InterstitialReframe />;
            case 'primary_goal':
                return <PrimaryGoalStep />;
            case 'main_barrier':
                return <MainBarrierStep />;
            case 'personalized_blockers':
                return <PersonalizedBlockersStep />;
            case 'testimonial_review':
                return <TestimonialReviewStep />;
            case 'interstitial_rewards':
                return <InterstitialRewards />;
            case 'reward_preference':
                return <RewardPreferenceStep />;
            case 'review_loading':
                return <ReviewLoadingStep />;
            case 'email_capture':
                return <EmailCaptureStep />;
            case 'referral_rewards':
                return <ReferralRewardsStep />;
            case 'journey_story':
                return <JourneyStoryStep />;
            case 'plan_loading':
                return <PlanLoadingStep />;
            case 'starting_point':
                return <StartingPointStep />;
            case 'plan_stages':
                return <PlanStagesStep />;
            case 'commitment_check':
                return <CommitmentCheckStep />;
            case 'expected_results':
                return <ExpectedResultsStep />;
            case 'results':
                return <ResultsPage />;
            case 'paywall':
                return <PaywallStep />;
            case 'payment': {
                const paymentElementsOptions = buildPaymentElementsOptions(data);
                return <Sentry.ErrorBoundary fallback={PaymentErrorFallback}><StripeProvider options={paymentElementsOptions}><PaymentStep onBeforeBack={handlePaymentBeforeBack} /></StripeProvider></Sentry.ErrorBoundary>;
            }
            case 'create_account':
                return <CreateAccountStep />;
            case 'coaching_upsell':
                return <ErrorBoundary><StripeProvider><CoachingUpsellStep /></StripeProvider></ErrorBoundary>;
            case 'exclusive_offer':
                return <ErrorBoundary><StripeProvider><ExclusiveOfferStep /></StripeProvider></ErrorBoundary>;
            case 'get_app':
                return <GetAppStep />;
            default:
                return null;
        }
    };

    return (
        <div className="w-full max-w-[430px] mx-auto min-h-dvh bg-white overflow-x-hidden">
            <FunnelV3ProgressBar currentStep={currentStep} setStep={setStep} />
            <div
                className={`transition-opacity duration-300 ease-in-out ${
                    isAnimating ? 'opacity-0' : 'opacity-100'
                }`}
            >
                <Suspense fallback={
                    <div className="min-h-dvh bg-white flex items-center justify-center">
                        <div className="w-8 h-8 border-3 border-[#369fff] border-t-transparent rounded-full animate-spin" />
                    </div>
                }>
                    {renderStep()}
                </Suspense>
            </div>

            {/* Exit intent overlay — shown when user clicks back from payment */}
            {showPaymentExitOverlay && (
                <Suspense fallback={null}>
                    <ExitIntentOverlay
                        isVisible
                        quizData={{
                            primaryGoal: data.primaryGoal as 'confidence' | 'health_energy' | 'clothes_fit' | 'sleep_stress' | undefined,
                            mainBarrier: data.mainBarrier as 'lose_motivation' | 'no_results_fast' | 'too_busy' | 'uncertainty' | undefined,
                            firstName: data.firstName || undefined,
                            weight: data.currentWeightKg ?? undefined,
                            weightGoal: data.goalWeightKg ?? undefined,
                            weightUnit: data.weightUnit === 'lbs' ? 'lb' : 'kg',
                            targetDate: data.targetDate ?? undefined,
                        }}
                        selectedPlan={(data.selectedPlan ?? '4_week') as PlanType}
                        onClaim={handlePaymentExitClaim}
                        onDismiss={handlePaymentExitDismiss}
                    />
                </Suspense>
            )}
        </div>
    );
};

export default FunnelV3Flow;
